import { AuthContextProps } from 'react-oidc-context';
import { setIsLoading, updateChatItem } from '../store/appSlice';
import { AppDispatch } from '../store/store';
import { postChatApi } from './api';
import { handleResponse } from './response';
import { ChatAnswerType } from '../store/types';
import { HttpStatus } from './http';
import {
  forbiddenErrorText,
  genericErrorText,
  rateLimitText,
  sessionExpiredInfoText,
  unauthorizedErrorText,
} from './constants';
import { ChatRequest } from './types';
import { normalizeWhitespace } from './utils';
import { HEADER_CHAT_SESSION_EXPIRED } from '../infrastructure/httpHeaders';

const getErrorDetailFromResponse = async (response: Response): Promise<string | undefined> => {
  try {
    const contentType = response.headers.get('content-type') ?? '';
    if (
      contentType.includes('application/json') ||
      contentType.includes('application/problem+json')
    ) {
      const body = await response.clone().json();
      if (body?.detail && typeof body.detail === 'string') {
        return body.detail;
      }
    }
  } catch {
    // Failed to parse error response body - fall back to default message
  }
  return undefined;
};

const getSessionExpiredNotice = (response: Response): string => {
  const sessionExpired = response.headers.get(HEADER_CHAT_SESSION_EXPIRED);
  return sessionExpired?.toLowerCase() === 'true' ? sessionExpiredInfoText : '';
};

export const handleRequest = (dispatch: AppDispatch, auth: AuthContextProps) => {
  return async (question: string, id?: number) => {
    const itemId = id ?? Date.now();

    const dispatchUpdate = (content: string, type: ChatAnswerType) => {
      dispatch(
        updateChatItem({
          id: itemId,
          question,
          answer: { content, type },
        })
      );
    };

    dispatch(setIsLoading(true));
    dispatchUpdate('', ChatAnswerType.loading);

    try {
      const request: ChatRequest = {
        message: normalizeWhitespace(question),
      };

      const response = await postChatApi(request, {
        accessToken: auth.user?.access_token,
      });

      if (response.status === HttpStatus.RateLimit) {
        const detail = await getErrorDetailFromResponse(response);
        dispatchUpdate(detail || rateLimitText, ChatAnswerType.error);
        return;
      }

      if (!response.body) {
        dispatchUpdate(genericErrorText, ChatAnswerType.error);
        return;
      }

      if (response.status == HttpStatus.Unauthorized) {
        dispatchUpdate(unauthorizedErrorText, ChatAnswerType.unauthorized);
        return;
      }

      if (response.status == HttpStatus.Forbidden) {
        dispatchUpdate(forbiddenErrorText, ChatAnswerType.forbidden);
        return;
      }

      const sessionExpiredNotice = getSessionExpiredNotice(response);

      // For server errors (5xx), try to extract the localized error from ProblemDetails
      if (response.status >= 500) {
        const detail = await getErrorDetailFromResponse(response);
        if (detail) {
          dispatchUpdate(detail, ChatAnswerType.error);
          return;
        }
      }

      await handleResponse(dispatch, sessionExpiredNotice)(response, itemId, question);
    } catch {
      dispatchUpdate(genericErrorText, ChatAnswerType.error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};
